import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import {
  Alert,
  Box,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { getUiLanguage, tr } from '../lib/i18n';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api, ApiError } from '../api/client';
import type { OrgUser, Role } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { PersonAvatar } from '../components/AccountMenu';
import { Button } from '../components/button';
import { ConfirmDialog, ErrorState } from '../components/common';
import { PageActions } from '../components/page-actions';
import { DataGrid, type GridColDef } from '../components/data-grid';
import { Select } from '../components/select';
import { PasswordField, TextField } from '../components/text-field';
import { useToast } from '../components/Toast';
import { EMPLOYEE_TYPE, fmtDate } from '../lib/labels';

const ASSIGNABLE = Object.keys(EMPLOYEE_TYPE).filter((t) => t !== 'OWNER');
const roleLabel = (role: Role) => EMPLOYEE_TYPE[role.code] ?? (getUiLanguage() === 'en' ? role.nameEn ?? role.name_en ?? role.name : role.name);

export function UsersPage() {
  const { can, me } = useAuth();
  const [params] = useSearchParams();
  const manage = can('users.manage');
  const list = useQuery({ queryKey: ['users'], queryFn: () => api<{ items: OrgUser[] }>('/users'), placeholderData: keepPreviousData });
  const [editing, setEditing] = useState<OrgUser | 'new' | null>(null);
  const [action, setAction] = useState<{ kind: 'disable' | 'reset' | 'owner' | 'enable'; u: OrgUser } | null>(null);
  const rows = list.data?.items ?? [];

  const columns = useMemo<GridColDef<OrgUser>[]>(() => [
    {
      field: 'full_name',
      headerName: tr('الموظف', 'Employee'),
      flex: 1.4,
      minWidth: 220,
      valueGetter: (_, u) => `${u.full_name} ${u.username}`,
      renderCell: ({ row: u }) => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
          <PersonAvatar name={u.full_name} seed={u.id} src={u.avatarUrl} size={36} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500} noWrap>{u.full_name}</Typography>
            <Typography variant="caption" color="text.secondary" component="div" noWrap><bdi>@{u.username}</bdi></Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: 'employee_type',
      headerName: tr('النوع', 'Type'),
      width: 130,
      type: 'singleSelect',
      valueOptions: Object.entries(EMPLOYEE_TYPE).map(([value, label]) => ({ value, label })),
      valueFormatter: (v) => EMPLOYEE_TYPE[String(v)] ?? String(v ?? ''),
    },
    {
      field: 'role_name',
      headerName: tr('الدور', 'Role'),
      width: 150,
      renderCell: ({ row: u }) => <Chip size="small" label={EMPLOYEE_TYPE[u.role_code] ?? (getUiLanguage() === 'en' ? u.role_name_en ?? u.role_name : u.role_name)} color={u.is_owner ? 'primary' : 'default'} variant={u.is_owner ? 'filled' : 'outlined'} />,
    },
    {
      field: 'phone',
      headerName: tr('التواصل', 'Contact'),
      flex: 1,
      minWidth: 160,
      valueGetter: (_, u) => `${u.phone ?? ''} ${u.email ?? ''}`,
      renderCell: ({ row: u }) => (
        <Box sx={{ minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.35 }}>
          <Typography variant="body2" dir="ltr">{u.phone ?? ''}</Typography>
          <Typography variant="caption" color="text.secondary">{u.email ?? ''}</Typography>
        </Box>
      ),
    },
    { field: 'open_requests', headerName: tr('طلبات مفتوحة', 'Open requests'), type: 'number', width: 140 },
    { field: 'last_login_at', headerName: tr('آخر دخول', 'Last login'), width: 180, valueFormatter: (v) => fmtDate(v as string | null) },
    {
      field: 'status',
      headerName: tr('الحالة', 'Status'),
      width: 120,
      type: 'singleSelect',
      valueOptions: [{ value: 'ACTIVE', label: tr('نشط', 'Active') }, { value: 'DISABLED', label: tr('موقوف', 'Disabled') }],
      renderCell: ({ row: u }) => <Chip size="small" label={u.status === 'ACTIVE' ? tr('نشط', 'Active') : tr('موقوف', 'Disabled')} color={u.status === 'ACTIVE' ? 'success' : 'default'} variant={u.status === 'ACTIVE' ? 'filled' : 'outlined'} />,
    },
    ...(manage ? [{
      field: 'actions',
      type: 'actions' as const,
      width: 56,
      getActions: ({ row: u }: { row: OrgUser }) => {
        if (u.is_owner && !me?.user.is_owner) return [];
        const items = [<GridActionsCellItem key="edit" icon={<EditIcon />} label={tr('تعديل', 'Edit')} onClick={() => setEditing(u)} showInMenu />];
        if (u.id !== me?.user.id) items.push(<GridActionsCellItem key="reset" icon={<LockResetIcon />} label={tr('إعادة تعيين كلمة المرور', 'Reset password')} onClick={() => setAction({ kind: 'reset', u })} showInMenu />);
        if (me?.user.is_owner && !u.is_owner && u.status === 'ACTIVE') items.push(<GridActionsCellItem key="owner" icon={<WorkspacePremiumIcon />} label={tr('نقل الملكية إليه', 'Transfer ownership')} onClick={() => setAction({ kind: 'owner', u })} showInMenu />);
        if (!u.is_owner && u.id !== me?.user.id) {
          items.push(u.status === 'ACTIVE'
            ? <GridActionsCellItem key="disable" icon={<BlockIcon />} label={tr('إيقاف الحساب', 'Disable account')} onClick={() => setAction({ kind: 'disable', u })} showInMenu />
            : <GridActionsCellItem key="enable" icon={<CheckCircleIcon />} label={tr('تفعيل الحساب', 'Enable account')} onClick={() => setAction({ kind: 'enable', u })} showInMenu />);
        }
        return items;
      },
    } satisfies GridColDef<OrgUser>] : []),
  ], [manage, me]);

  return (
    <>
      {manage && (
        <PageActions>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setEditing('new')}>{tr('موظف جديد', 'New employee')}</Button>
        </PageActions>
      )}
      <Card sx={{ flex: 1, minHeight: 420, overflow: 'hidden' }}>
        {list.isError ? <Box sx={{ p: 2 }}><ErrorState error={list.error} onRetry={() => list.refetch()} /></Box> : (
          <DataGrid
            rows={rows}
            columns={columns}
            loading={list.isLoading}
            getRowClassName={(p) => (p.row.status === 'DISABLED' ? 'row-disabled' : '')}
            sx={{ '& .row-disabled': { opacity: 0.6 } }}
            quickFilter={params.get('q')}
            rowHeight={60}
          />
        )}
      </Card>

      {editing && <UserDialog user={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {action?.kind === 'disable' && <DisableDialog u={action.u} users={rows} onClose={() => setAction(null)} />}
      {action?.kind === 'reset' && <ResetPasswordDialog u={action.u} onClose={() => setAction(null)} />}
      {(action?.kind === 'owner' || action?.kind === 'enable') && <SimpleAction kind={action.kind} u={action.u} onClose={() => setAction(null)} />}
    </>
  );
}

function useInvalidateUsers() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ['users'] });
}

function UserDialog({ user, onClose }: { user: OrgUser | null; onClose: () => void }) {
  const { toast, toastError } = useToast();
  const invalidate = useInvalidateUsers();
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api<{ items: Role[] }>('/roles') });
  const [v, setV] = useState({
    fullName: user?.full_name ?? '',
    username: user?.username ?? '',
    password: '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    employeeType: user?.employee_type ?? 'SALES',
    roleId: user?.role_id ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      user
        ? api(`/users/${user.id}`, {
            method: 'PATCH',
            body: {
              fullName: v.fullName.trim(),
              email: v.email.trim() || null,
              phone: v.phone.trim() || null,
              ...(!user.is_owner && { employeeType: v.employeeType, ...(v.roleId && v.roleId !== user.role_id ? { roleId: v.roleId } : {}) }),
            },
          })
        : api('/users', {
            method: 'POST',
            body: {
              fullName: v.fullName.trim(),
              username: v.username.trim(),
              password: v.password,
              email: v.email.trim() || undefined,
              phone: v.phone.trim() || undefined,
              employeeType: v.employeeType,
              roleId: v.roleId || undefined,
            },
          }),
    onSuccess: () => { toast(user ? tr('تم حفظ التعديلات', 'Changes saved') : tr('تمت إضافة الموظف — سيُطلب منه تغيير كلمة المرور عند أول دخول', 'Employee added — they will be asked to change their password on first login')); invalidate(); onClose(); },
    onError: (e) => {
      if (e instanceof ApiError && Object.keys(e.fieldErrors()).length) setErrors(e.fieldErrors());
      toastError(e);
    },
  });
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => { setV({ ...v, [k]: e.target.value }); setErrors({ ...errors, [k]: '' }); };
  const err = (k: string) => ({ error: !!errors[k], helperText: errors[k] || undefined });
  const assignableRoles = (roles.data?.items ?? []).filter((r) => r.code !== 'OWNER');
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{user ? tr(`تعديل ${user.full_name}`, `Edit ${user.full_name}`) : tr('موظف جديد', 'New employee')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {save.isError && !Object.keys(errors).length && (
            <Grid size={12}><Alert severity="error">{save.error instanceof Error ? save.error.message : tr('تعذر الحفظ', 'Unable to save')}</Alert></Grid>
          )}
          <Grid size={12}><TextField fullWidth label={tr('الاسم الكامل', 'Full name')} value={v.fullName} onChange={set('fullName')} {...err('fullName')} autoFocus /></Grid>
          {!user && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={tr('اسم المستخدم', 'Username')} value={v.username} onChange={set('username')} {...err('username')} slotProps={{ htmlInput: { dir: 'ltr' } }} helperText={errors.username || tr('أحرف إنجليزية وأرقام و . _ -', 'Letters, numbers, and . _ -')} /></Grid>
              <Grid size={{ xs: 12, sm: 6 }}><PasswordField fullWidth label={tr('كلمة مرور مؤقتة', 'Temporary password')} value={v.password} onChange={set('password')} {...err('password')} helperText={errors.password || tr('8 أحرف على الأقل، حروف وأرقام', 'At least 8 characters with letters and numbers')} /></Grid>
            </>
          )}
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={tr('الهاتف', 'Phone')} value={v.phone} onChange={set('phone')} {...err('phone')} slotProps={{ htmlInput: { dir: 'ltr' } }} /></Grid>
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={tr('البريد (اختياري)', 'Email (optional)')} value={v.email} onChange={set('email')} {...err('email')} slotProps={{ htmlInput: { dir: 'ltr' } }} /></Grid>
          {user?.is_owner ? null : (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Select fullWidth label={tr('نوع الموظف', 'Employee type')} value={v.employeeType} onChange={(e) => setV({ ...v, employeeType: e.target.value, roleId: '' })} helperText={tr('يحدد الدور الافتراضي', 'Determines the default role')}>
                  {ASSIGNABLE.map((t) => <MenuItem key={t} value={t}>{EMPLOYEE_TYPE[t]}</MenuItem>)}
                </Select>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Select fullWidth label={tr('الدور (الصلاحيات)', 'Role (permissions)')} value={v.roleId} onChange={(e) => { setV({ ...v, roleId: e.target.value }); setErrors({ ...errors, roleId: '' }); }} error={!!errors.roleId} helperText={errors.roleId || tr('اتركه للدور الافتراضي حسب النوع', 'Leave empty to use the default role for this type')}>
                  <MenuItem value="">{tr('الدور الافتراضي للنوع', 'Default role for type')}</MenuItem>
                  {assignableRoles.map((r) => <MenuItem key={r.id} value={r.id}>{roleLabel(r)}{r.is_system ? '' : tr(' (مخصص)', ' (custom)')}</MenuItem>)}
                </Select>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tr('إلغاء', 'Cancel')}</Button>
        <Button variant="contained" onClick={() => save.mutate()} loading={save.isPending} disabled={!v.fullName.trim() || (!user && (!v.username.trim() || !v.password))}>{tr('حفظ', 'Save')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DisableDialog({ u, users, onClose }: { u: OrgUser; users: OrgUser[]; onClose: () => void }) {
  const { toast, toastError } = useToast();
  const invalidate = useInvalidateUsers();
  const qc = useQueryClient();
  const [to, setTo] = useState('');
  const m = useMutation({
    mutationFn: () => api<{ reassignedRequests: number }>(`/users/${u.id}/disable`, { method: 'POST', body: { reassignTo: to || null } }),
    onSuccess: (r) => {
      toast(tr(`تم إيقاف الحساب${r.reassignedRequests ? ` ونقل ${r.reassignedRequests} طلب` : ''}`, `Account disabled${r.reassignedRequests ? ` and ${r.reassignedRequests} requests reassigned` : ''}`));
      invalidate();
      void qc.invalidateQueries({ queryKey: ['requests'] });
      onClose();
    },
    onError: toastError,
  });
  const others = users.filter((x) => x.id !== u.id && x.status === 'ACTIVE');
  return (
    <ConfirmDialog open danger title={tr(`إيقاف ${u.full_name}`, `Disable ${u.full_name}`)} confirmLabel={tr('إيقاف الحساب', 'Disable account')} busy={m.isPending} onConfirm={() => m.mutate()} onClose={onClose}
      text={<>{tr('سيتم تسجيل خروجه فوراً ولن يستطيع الدخول.', 'They will be signed out immediately and will not be able to sign in.')}{u.open_requests > 0 && <> {tr('لديه', 'They have')} <b>{u.open_requests}</b> {tr('طلب مفتوح — اختر لمن تُنقل:', 'open requests — choose who should receive them:')}</>}</>}
    >
      {u.open_requests > 0 && (
        <Select fullWidth label={tr('نقل الطلبات المفتوحة إلى', 'Reassign open requests to')} value={to} onChange={(e) => setTo(e.target.value)} sx={{ mt: 2 }}>
          <MenuItem value="">{tr('تركها غير معيّنة', 'Leave unassigned')}</MenuItem>
          {others.map((x) => <MenuItem key={x.id} value={x.id}>{x.full_name}</MenuItem>)}
        </Select>
      )}
    </ConfirmDialog>
  );
}

function ResetPasswordDialog({ u, onClose }: { u: OrgUser; onClose: () => void }) {
  const { toast, toastError } = useToast();
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const m = useMutation({
    mutationFn: () => api(`/users/${u.id}/reset-password`, { method: 'POST', body: { newPassword: pw } }),
    onSuccess: () => { toast(tr('تم تعيين كلمة مرور مؤقتة — أرسلها للموظف', 'Temporary password set — send it to the employee')); onClose(); },
    onError: (e) => { if (e instanceof ApiError) setErr(e.fieldErrors().newPassword ?? e.message); else toastError(e); },
  });
  return (
    <ConfirmDialog open title={tr(`كلمة مرور جديدة لـ ${u.full_name}`, `New password for ${u.full_name}`)} confirmLabel={tr('تعيين', 'Set password')} busy={m.isPending} disabled={pw.length < 8} onConfirm={() => m.mutate()} onClose={onClose}
      text={tr('ستنتهي جلساته الحالية، وسيُطلب منه تغييرها عند الدخول.', 'Their current sessions will end and they will be asked to change it at sign-in.')}>
      {err && <Alert severity="error">{err}</Alert>}
      <PasswordField fullWidth autoFocus label={tr('كلمة المرور المؤقتة', 'Temporary password')} value={pw} onChange={(e) => { setPw(e.target.value); setErr(''); }} error={!!err} helperText={err ? undefined : tr('8 أحرف على الأقل، حروف وأرقام', 'At least 8 characters with letters and numbers')} />
    </ConfirmDialog>
  );
}

function SimpleAction({ kind, u, onClose }: { kind: 'owner' | 'enable'; u: OrgUser; onClose: () => void }) {
  const { toast, toastError } = useToast();
  const { reload } = useAuth();
  const invalidate = useInvalidateUsers();
  const m = useMutation({
    mutationFn: () => api(`/users/${u.id}/${kind === 'owner' ? 'make-owner' : 'enable'}`, { method: 'POST', body: {} }),
    onSuccess: async () => {
      toast(kind === 'owner' ? tr('تم نقل الملكية', 'Ownership transferred') : tr('تم تفعيل الحساب', 'Account enabled'));
      invalidate();
      onClose();
      if (kind === 'owner') await reload().catch(() => undefined);
    },
    onError: toastError,
  });
  return kind === 'owner' ? (
    <ConfirmDialog open danger title={tr('نقل ملكية الشركة', 'Transfer organization ownership')} confirmLabel={tr('نقل الملكية', 'Transfer ownership')} busy={m.isPending} onConfirm={() => m.mutate()} onClose={onClose}
      text={<>{tr('سيصبح', '')} <b>{u.full_name}</b> {tr('مالك الشركة بكل الصلاحيات، وتتحول أنت إلى «مدير». لا يمكنك التراجع بنفسك.', 'will become the organization owner with full permissions. You will become a Manager and cannot reverse this yourself.')}</>} />
  ) : (
    <ConfirmDialog open title={tr(`تفعيل ${u.full_name}`, `Enable ${u.full_name}`)} confirmLabel={tr('تفعيل', 'Enable')} busy={m.isPending} onConfirm={() => m.mutate()} onClose={onClose} text={tr('سيتمكن من الدخول مجدداً بنفس الدور.', 'They will be able to sign in again with the same role.')} />
  );
}
